import React, {
  forwardRef,
  useRef,
  AnchorHTMLAttributes,
  useMemo,
} from 'react';
import { tabStyles, TabIconLabelWrapper } from '#components/tabs/tab.tsx';
import { useTabElementIsInView } from '#components/tabs/use-tab-element-is-in-view.tsx';
import { useTablistContext } from '#components/tabs/tablist-context.tsx';
import { Icon, GDSIconSource } from '#components/icon/icon.tsx';
import { styled } from '../../theme';
import { useResponsiveProp } from '../../hooks/use-responsive-prop';
import { useComposedRefs } from '../../hooks/use-composed-ref';
import type { GDSCustomizableComponent, GDSResponsiveProp } from '../../types';
import { GenesisCoreInspector } from '../../test-utils/genesis-core-inspector';

export interface GDSLinkProps
  extends AnchorHTMLAttributes<HTMLAnchorElement>,
    GDSCustomizableComponent {
  icon?: GDSIconSource;
  iconLabel?: string;
  width?: GDSResponsiveProp<number | string>;
}

const DISPLAY_NAME = 'NavItem';

const BaseNavLink = styled('a', {
  ...tabStyles,
});

export const NavItem = styled('li', {
  listStyle: 'none',
});

export const Link = forwardRef<HTMLAnchorElement, GDSLinkProps>(
  (
    {
      children,
      className,
      css,
      href,
      icon,
      iconLabel,
      target,
      width,
      'data-testid': testId = `GDS-${DISPLAY_NAME}`,
      ...props
    },
    ref,
  ) => {
    const internalRef = useRef<HTMLAnchorElement>(null);
    const composedRefs = useComposedRefs(ref, internalRef);
    const tabWidth = useResponsiveProp(width);
    const tabIcon = useResponsiveProp(icon);
    const { elementIsInView, tabScrollOptions } = useTabElementIsInView();
    const { scrollBackButton, scrollForwardButton } = useTablistContext();

    const scrollElementIfNotInView = useMemo(
      () => () => {
        if (
          !elementIsInView(internalRef) &&
          (scrollBackButton || scrollForwardButton)
        ) {
          internalRef.current!.scrollIntoView(tabScrollOptions);
        }
      },
      [
        elementIsInView,
        tabScrollOptions,
        scrollBackButton,
        scrollForwardButton,
      ],
    );

    return (
      <GenesisCoreInspector displayName={DISPLAY_NAME}>
        <NavItem
          className={[`GDS-nav-item`, className].join(' ')}
          css={{
            ...css,
            ...(width && {
              width: `${tabWidth}`,
            }),
          }}
          data-testid={testId}
        >
          <BaseNavLink
            className="GDS-nav-link"
            href={href}
            target={target}
            ref={composedRefs}
            onFocus={() => scrollElementIfNotInView()}
            {...props}
          >
            <TabIconLabelWrapper>
              {tabIcon && <Icon icon={tabIcon} label={iconLabel} />}
              {children}
            </TabIconLabelWrapper>
          </BaseNavLink>
        </NavItem>
      </GenesisCoreInspector>
    );
  },
);
